import type {Meta, Value} from "./best_effort_store"
import type {PersistentWorkQueue} from "./persistent_work_queue"
import {from_buffer, to_uint8array} from "@cling/lib.shared.model"
import {decode_b64url, encode_b64url} from "@cling/lib.shared.utils"
import {RateLimiter} from "@cling/lib.shared.utils/rate_limiter"
import {report_error, report_info} from "@cling/lib.shared.debug"
import {Buffer} from "buffer"

export class LocalStorageUnavailable extends Error {
    constructor(name: string, cause: Error) {
        super(`[${name}] localStorage is not usable: ${cause.message}`)
        // This is needed according to https://goo.gl/N2zvkR
        Object.setPrototypeOf(this, LocalStorageUnavailable.prototype)
    }
}

export interface LocalStorageWorkQueue<META extends Meta> extends PersistentWorkQueue<META> {
    /**
     * `min_age` overrides the queue's own for this call, so a caller that knows nothing can be in
     * flight (right after a page load, say) does not have to wait it out.
     */
    step(
        handle_batch: (
            batch: Array<{key: string; value: Uint8Array; metadata: META}>,
        ) => Promise<{remove_keys: Array<string>}>,
        opts?: {batch_size: number; min_age?: number},
    ): Promise<void>
    /**
     * Remove entries anywhere in the queue without going through `step`.
     */
    remove(match: (entry: {key: string; metadata: META}) => boolean): void
    size(): number
    /** What the queue occupies in `storage`, in characters. */
    bytes(): number
}

/**
 * A {@link PersistentWorkQueue} backed by `localStorage`.
 *
 * Updates are synchronous, so an item is durable as soon as `enqueue` or `remove` is called.
 *
 * @param min_age `step` ignore entries younger than this.
 * @param max_age entries older than this are discarded
 * @throws {LocalStorageUnavailable} if `storage` cannot be written to at all.
 */
export function local_storage_work_queue<META extends Meta>({
    name,
    metadata_type,
    min_age = 30_000,
    max_age = 30 * 24 * 60 * 60_000,
    storage = localStorage,
}: {
    name: string
    metadata_type: {_from_pb: (p: any) => META}
    min_age?: number
    max_age?: number
    storage?: Storage
}): LocalStorageWorkQueue<META> {
    const item_prefix = `cling.wq/${name}/i/`
    const probe_key = `cling.wq/${name}/probe`
    // Test if localStorage works.
    try {
        storage.setItem(probe_key, "1")
        storage.removeItem(probe_key)
    } catch (error) {
        throw new LocalStorageUnavailable(name, error)
    }
    // Every tab shares one `localStorage`, so the key needs a per-instance part. Without it two
    // tabs enqueueing in the same millisecond would overwrite each other's work.
    const instance = Math.random().toString(36).slice(2, 8).padEnd(6, "0")
    let local_counter = 0
    const log_process_limiter = new RateLimiter({max_calls: 1, per: [5, "minutes"]})
    const log_discard_limiter = new RateLimiter({max_calls: 1, per: [5, "minutes"]})

    function item_keys(): Array<string> {
        const keys = []
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i)
            if (key?.startsWith(item_prefix)) {
                keys.push(key)
            }
        }
        return keys.sort()
    }

    /**
     * `undefined` if the key carries no usable timestamp. Such an entry is always handed out and
     * never expires, so a key we do not understand can never silently swallow someone's work.
     */
    function age(key: string, now: number): number | undefined {
        const enqueued_at = Number(key.slice(item_prefix.length).split(":")[0])
        return Number.isNaN(enqueued_at) ? undefined : now - enqueued_at
    }

    function read_metadata(key: string): META | undefined {
        const metadata = storage.getItem(key)?.split(".")[1]
        if (metadata === undefined) {
            return undefined
        }
        try {
            return from_buffer(metadata_type, decode_b64url(metadata))
        } catch {
            return undefined
        }
    }

    function read_item(key: string): Value<META> | undefined {
        const value = storage.getItem(key)?.split(".")[0]
        const metadata = read_metadata(key)
        if (value === undefined || metadata === undefined) {
            return undefined
        }
        try {
            return {value: new Uint8Array(decode_b64url(value)), metadata}
        } catch {
            return undefined
        }
    }

    return {
        // This is `async` to match `PersistentWorkQueue`, but the item is written before the call
        // returns. A failure (a full `localStorage`, most likely) surfaces as a rejected promise
        // and is the caller's to handle.
        enqueue: async (value: Uint8Array, metadata: META) => {
            local_counter += 1
            // Make sure the key can be sorted lexicographically and that digits do not overflow
            // (that's why we pad the string).
            const key =
                `${item_prefix}${Date.now().toString().padStart(16, "0")}` +
                `:${local_counter.toString().padStart(10, "0")}:${instance}`
            const encoded =
                `${encode_b64url(Buffer.from(value))}.` +
                `${encode_b64url(Buffer.from(to_uint8array(metadata)))}`
            storage.setItem(key, encoded)
        },
        step: async (
            handle_batch: (
                batch: Array<{key: string; value: Uint8Array; metadata: META}>,
            ) => Promise<{remove_keys: Array<string>}>,
            opts: {batch_size: number; min_age?: number} = {batch_size: 1},
        ) => {
            const now = Date.now()
            const fresh: Array<string> = []
            let num_expired = 0
            let oldest_expired = 0
            for (const key of item_keys()) {
                const item_age = age(key, now)
                if (item_age !== undefined && item_age >= max_age) {
                    num_expired += 1
                    oldest_expired = Math.max(oldest_expired, item_age)
                    storage.removeItem(key)
                } else {
                    fresh.push(key)
                }
            }
            // Entries are keyed by their enqueue time, so the ones old enough are always a prefix
            // and FIFO order is kept.
            const keys = fresh
                .filter((x) => (age(x, now) ?? Infinity) >= (opts.min_age ?? min_age))
                .slice(0, opts.batch_size)
            const batch = []
            let num_unreadable = 0
            for (const key of keys) {
                const item = read_item(key)
                if (!item) {
                    // This is not a work item any more, so no caller could ever act on it, and
                    // keeping it would block the queue forever.
                    num_unreadable += 1
                    storage.removeItem(key)
                    continue
                }
                batch.push({key, ...item})
            }
            if (num_expired || num_unreadable) {
                log_discard_limiter.run((within_limit) => {
                    if (within_limit) {
                        report_error(`[${name}] Discarded work items`, {
                            num_expired,
                            oldest_expired,
                            num_unreadable,
                        })
                    }
                })
            }
            if (batch.length === 0) {
                return
            }
            log_process_limiter.run((within_limit) => {
                if (within_limit) {
                    report_info(`[${name}] Processing work items`, {
                        num_handed_over: batch.length,
                        size: fresh.length,
                        oldest: age(fresh[0], now),
                    })
                }
            })
            const {remove_keys} = await handle_batch(batch)
            for (const key of remove_keys) {
                storage.removeItem(key)
            }
        },
        remove: (match: (entry: {key: string; metadata: META}) => boolean) => {
            for (const key of item_keys()) {
                const metadata = read_metadata(key)
                if (metadata && match({key, metadata})) {
                    storage.removeItem(key)
                }
            }
        },
        size: () => item_keys().length,
        bytes: () =>
            item_keys().reduce((n, key) => n + key.length + (storage.getItem(key)?.length ?? 0), 0),
    }
}
